/**
 * Theme Debug Utility
 * 
 * Centralized logging for theme-related events.
 * Enable via: localStorage.setItem('debug:theme', 'true')
 * 
 * Per theme-architecture.md V2:
 * - Authorized writers: index.html, ThemeSwitcher, Onboarding (complete only), UserContext (first load)
 * - Forbidden: Preview clicks, page mounts, auth transitions, route changes
 */

const isEnabled = () => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('debug:theme') === 'true';
};

const PREFIX = '[Theme]';

export const themeDebug = {
  /**
   * Log theme application event
   */
  applied: (source: string, theme: string, reason?: string) => {
    if (!isEnabled()) return;
    console.log(
      `${PREFIX} ✅ APPLIED`,
      `\n  Source: ${source}`,
      `\n  Theme: ${theme}`,
      reason ? `\n  Reason: ${reason}` : ''
    );
  },

  /**
   * Log when theme application was skipped
   */
  skipped: (source: string, reason: string) => {
    if (!isEnabled()) return;
    console.log(
      `${PREFIX} ⏭️ SKIPPED`,
      `\n  Source: ${source}`,
      `\n  Reason: ${reason}`
    );
  },

  /**
   * Log forbidden theme write attempt (violation)
   */
  violation: (source: string, attemptedAction: string) => {
    // Always log violations, even if debug is disabled
    console.warn(
      `${PREFIX} 🚨 VIOLATION DETECTED`,
      `\n  Source: ${source}`,
      `\n  Attempted: ${attemptedAction}`,
      `\n  See docs/theme-architecture.md for allowed writers`
    );
  },

  /**
   * Log theme preview (state-only, no mutation)
   */
  preview: (themeId: string) => {
    if (!isEnabled()) return;
    console.log(
      `${PREFIX} 👁️ PREVIEW (state only)`,
      `\n  Theme: ${themeId}`
    );
  },

  /**
   * Log theme sync from database
   */
  dbSync: (dbTheme: string, action: 'applied' | 'skipped', reason: string) => {
    if (!isEnabled()) return;
    console.log(
      `${PREFIX} 🔄 DB SYNC`,
      `\n  DB Theme: ${dbTheme}`,
      `\n  Action: ${action}`,
      `\n  Reason: ${reason}`
    );
  },

  /**
   * Log premium downgrade
   */
  downgrade: (from: string, to: string) => {
    if (!isEnabled()) return;
    console.log(
      `${PREFIX} ⬇️ DOWNGRADE`,
      `\n  From: ${from}`,
      `\n  To: ${to}`,
      `\n  Reason: Premium expired`
    );
  },

  /**
   * Enable theme debugging
   */
  enable: () => {
    localStorage.setItem('debug:theme', 'true');
    console.log(`${PREFIX} Debug enabled. Refresh to see theme events.`);
  },

  /**
   * Disable theme debugging
   */
  disable: () => {
    localStorage.removeItem('debug:theme');
    console.log(`${PREFIX} Debug disabled.`);
  },

  /**
   * Show current theme state
   */
  status: () => {
    const localTheme = localStorage.getItem('studyforge-theme');
    const htmlClass = document.documentElement.classList.toString();
    console.log(
      `${PREFIX} 📊 STATUS`,
      `\n  localStorage: ${localTheme || '(none)'}`,
      `\n  <html> class: ${htmlClass}`
    );
  }
};

// Expose to window for console debugging
if (typeof window !== 'undefined') {
  (window as any).themeDebug = themeDebug;
}
